import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wrench, Users, FileText, Package, MessageSquare,
  BarChart3, Settings, LogOut, Plus, Search, Store, Smartphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import logoBlack from "@/assets/logo-black.png";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: null },
  { to: "/repairs", label: "Riparazioni", icon: Wrench, perm: "repairs.view" },
  { to: "/customers", label: "Clienti", icon: Users, perm: "customers.view" },
  { to: "/quotes", label: "Preventivi", icon: FileText, perm: "quotes.view" },
  { to: "/inventory", label: "Magazzino", icon: Package, perm: "inventory.view" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageSquare, perm: "whatsapp.view" },
  { to: "/reports", label: "Report", icon: BarChart3, perm: "reports.view" },
  { to: "/settings", label: "Impostazioni", icon: Settings, perm: "users.view" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("all");
  const [query, setQuery] = useState("");

  const isGlobal = user?.role === "admin";

  useEffect(() => {
    api.get("/stores").then(({ data }) => {
      setStores(data);
      if (!isGlobal && user?.store_id) setSelectedStore(user.store_id);
    });
  }, [isGlobal, user]);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/repairs?q=${encodeURIComponent(query.trim())}`);
  };

  const storeValue = useMemo(
    () => ({ stores, selectedStore, setSelectedStore }),
    [stores, selectedStore]
  );

  return (
    <StoreCtx.Provider value={storeValue}>
      <div className="min-h-screen bg-background">
        {/* Sidebar */}
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card lg:flex">
          <div className="flex flex-col gap-1 px-6 py-5 border-b border-border">
            <img src={logoBlack} alt="Dexter Lab" className="h-8 w-auto self-start object-contain" data-testid="sidebar-logo" />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gestionale</div>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {NAV.filter((n) => !n.perm || can(n.perm)).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-border p-4">
            <div className="mb-3 text-sm">
              <div className="font-semibold">{user?.name} {user?.surname}</div>
              <div className="text-xs text-muted-foreground">{user?.role_label}</div>
            </div>
            <Button variant="outline" size="sm" className="w-full" data-testid="logout-button"
              onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" /> Esci
            </Button>
          </div>
        </aside>

        {/* Main */}
        <div className="lg:pl-64">
          <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-border bg-card/90 px-4 py-3 backdrop-blur-md sm:px-6">
            <form onSubmit={onSearch} className="relative flex-1 min-w-[200px] max-w-xl">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="global-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca cliente, telefono, IMEI, n° pratica..."
                className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </form>

            {isGlobal && (
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger className="w-[220px]" data-testid="store-selector">
                  <Store className="mr-2 h-4 w-4" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i negozi</SelectItem>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {can("repairs.create") && (
              <Button data-testid="new-repair-button" onClick={() => navigate("/repairs/new")}>
                <Plus className="mr-2 h-4 w-4" /> Nuova riparazione
              </Button>
            )}
          </header>

          <main className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </StoreCtx.Provider>
  );
}
